import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import EmbedPDF, { EmbedPdfContainer } from '@embedpdf/snippet';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { PdfType } from '../models/pdf-type';
import { ApiConfiguration } from '../../api/api-configuration';
import { StateService } from '../../services/state.service';
import { ToolService } from '../../services/tool.service';
@Component({
  selector: 'lib-viewer',
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.scss'],
  standalone: false,
})
export class ViewerComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);

  docContentPage: number = 1;
  @ViewChild('viewerContainer', { static: true })
  viewerContainer: ElementRef<HTMLDivElement>;
  pdfjsIframe: HTMLIFrameElement;
  @Input() docId: string;
  @Input() pdfType: string;
  @Input() fileExtension: string;
  @Input() docName: string;
  isPdf: boolean = false;
  isImage: boolean = false;
  isVideo: boolean = false;
  imageExtensions: string[] = ['.jpg', '.jpeg', '.png'];
  hideLoader: boolean = false;
  loader: HTMLIonLoadingElement;
  localUrl: string;
  file: Blob;
  embedInstance: EmbedPdfContainer;
  annotationPlugin: any;
  pdfJSRunning: boolean = true;
  private pdfJsSrc: string = '';
  constructor(
    private base: ApiConfiguration,
    public state: StateService,
    private tools: ToolService
  ) {}

  private messageHandler = async (event: MessageEvent) => {
    if (event.data?.type === 'documentloaded') {
      await this.loader?.dismiss();
      return;
    }

    if (event.data?.type === 'documentdownload') {
      console.log('download', this.docName);
      await this.downloadFile();
    }
  };

  async ngOnInit() {
    window.addEventListener('message', this.messageHandler);
    await this.loadFile(
      this.docId,
      this.pdfType,
      this.fileExtension,
      this.docName
    );
  }

  public async loadFile(
    docId: string,
    pdfType: string,
    fileExtension: string,
    docName: string
  ) {
    this.docId = docId;
    this.pdfType = pdfType;
    this.fileExtension = fileExtension;
    this.docName = docName;
    this.isPdf = this.fileExtension.toLowerCase() == '.pdf';
    this.isImage = this.imageExtensions.includes(
      this.fileExtension.toLowerCase()
    );
    this.isVideo = this.fileExtension.toLowerCase() == '.mp4';
    if (this.isPdf && this.pdfType != 'document') {
      this.loader = await this.tools.showLoader(
        this.translate.instant('reports.generating')
      );
      this.loader.present();
    }
    this.state.downloadBinary(this.docId, this.pdfType).subscribe({
      next: async (x) => {
        this.file = x.blob;
        this.localUrl = x.url;
        if (this.isPdf) {
          this.pdfJsSrc = `/assets/pdfjs/web/viewer.html?file=${encodeURIComponent(
            this.localUrl
          )}&filename=${encodeURIComponent(this.docName)}`;
          if (
            this.pdfjsIframe != null &&
            this.pdfjsIframe.src.includes(this.pdfJsSrc)
          ) {
            return;
          }
          if (this.pdfType == PdfType.Document) {
            this.loader = await this.tools.showLoader('Dokument wird geladen');
            this.loader.present();
          }
          this.loadPdfJs();
          //this.loadEmbedPdf();
        }
      },
      error: (err) => {
        this.state.handleError(err);
      },
    });
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.messageHandler);
  }

  loadPdfJs(): void {
    this.pdfJSRunning = true;

    if (this.pdfjsIframe) {
      this.pdfjsIframe.src = this.pdfJsSrc;
      return;
    }
    const iFrame = document.createElement('iframe');
    iFrame.style.width = '100%';
    iFrame.style.height = '100%';
    iFrame.style.border = 'none';

    iFrame.src = this.pdfJsSrc;

    this.viewerContainer.nativeElement.appendChild(iFrame);
    this.pdfjsIframe = iFrame;
  }

  async loadEmbedPdf(): Promise<void> {
    this.pdfJSRunning = false;
    this.embedInstance = EmbedPDF.init({
      type: 'container',
      target: this.viewerContainer.nativeElement,
      src: this.localUrl,
      annotations: {
        annotationAuthor: this.state.getUserDisplayName(this.state.userName),
        autoCommit: false,
        selectAfterCreate: false,
      },
      theme: { preference: 'system' },
    });
    const registry = await this.embedInstance.registry;
    const annotationPlugin = registry.getPlugin('annotation').provides();

    annotationPlugin.onAnnotationEvent((annot: any) => {
      console.log(JSON.stringify(annot));
    });
  }

  impportAnnotations() {
    //this.annotationPlugin.importAnnotations([]);
  }

  private async destroyPdfJs(): Promise<void> {
    if (this.pdfjsIframe) {
      this.pdfjsIframe.remove();
      this.pdfjsIframe = undefined;
    }
  }

  private async destroyEmbedPdf(): Promise<void> {
    if (this.embedInstance) {
      await this.embedInstance.remove();
      this.embedInstance = undefined;
    }
  }

  async changeViewer(): Promise<void> {
    if (this.pdfJSRunning) {
      await this.destroyPdfJs();
      await this.loadEmbedPdf();
    } else {
      await this.destroyEmbedPdf();
      this.loadPdfJs();
    }
  }

  loadNative() {
    const iFrame = document.createElement('iframe');
    iFrame.style.width = '100%';
    iFrame.style.height = '100%';
    iFrame.style.border = 'none';
    iFrame.allowFullscreen = true;
    iFrame.src = `${this.getFileSource()}`;

    this.viewerContainer.nativeElement.appendChild(iFrame);
  }

  getFileSource(): string {
    return this.docId
      ? `${this.base.rootUrl}/api/v0/binary/${this.pdfType}/${this.docId}/content`
      : '';
  }

  getFileVideoSource(): string {
    return this.docId
      ? `${this.base.rootUrl}/api/v0/binary/${this.pdfType}/${this.docId}/content`
      : '';
    // add #t=0.1
  }

  //hopefully this feature can be deleted and the capacitor/share package uninstalled when pdf annotations are working
  public async downloadFile() {
    const base64Data = await this.tools.blobToBase64(this.file);

    const result = await Filesystem.writeFile({
      path: this.docName + this.fileExtension,
      data: base64Data,
      directory: Directory.Documents,
    });

    if (this.state.isIOS) {
      const shareResult = await Share.share({
        title: this.docName + this.fileExtension,
        url: result.uri,
      });
    } else {
      if (result && result.uri) {
        await this.tools.showInfoToast(
          this.translate.instant('files.storedSuccess')
        );
      } else {
        await this.tools.showErrorToast(
          this.translate.instant('files.storeError', {
            error: JSON.stringify(result),
          })
        );
      }
    }
  }
}
